import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Skeleton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import { useTheme } from "@mui/material/styles";
import { useGetAttachmentUrlMutation } from "@/features/Global/api";

interface Template {
  id: string;
  name: string;
  description: string;
  image: string;
  color: string;
  imageKey: string;
}

interface TemplateSelectionPageProps {
  onBack: () => void;
  onTemplateSelect: (templateId: string, sprintId: string) => void;
  selectedSprintId: string;
  selectedSprintName: string;
}

const TemplateSelectionPage: React.FC<TemplateSelectionPageProps> = ({
  onBack,
  onTemplateSelect,
  selectedSprintId,
  selectedSprintName,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loadingImages, setLoadingImages] = useState<{
    [key: string]: boolean;
  }>({});
  const [templatesLoaded, setTemplatesLoaded] = useState(false);

  const [postAttachmentKey, { isLoading: isLoadingAttachment }] =
    useGetAttachmentUrlMutation();

  const initialTemplates: Template[] = [
    {
      id: "4l-retrospective",
      name: "4Ls Retrospective",
      description:
        "What did you Like, Learn, Lack, and Long for? A positive approach to gather team feedback.",
      imageKey:
        "dev/OTHERS/962e5384-89e7-41c3-ba6b-ad697a8d0e64/6825874c015624d10f1308f0/Screenshot 2025-09-03 at 6.29.13 PM_1756904437139_elc94i6y4dm.png",
      color: "#4CAF50",
      image: "",
    },
    {
      id: "board_1",
      name: "Blank Board",
      description:
        "Start with a blank canvas and customize your retrospective as you go.",
      imageKey:
        "dev/OTHERS/962e5384-89e7-41c3-ba6b-ad697a8d0e64/6825874c015624d10f1308f0/Screenshot 2025-09-03 at 6.28.24 PM_1756904437129_82kp1uvy53v.png",
      color: "",
      image: "",
    },
    {
      id: "start-stop-continue",
      name: "Start, Stop, Continue",
      description:
        "Identify what to start doing, stop doing, and continue doing for better team performance.",
      imageKey:
        "dev/OTHERS/962e5384-89e7-41c3-ba6b-ad697a8d0e64/6825874c015624d10f1308f0/Screenshot 2025-09-03 at 6.29.59 PM_1756904437145_j948ptsnj0h.png",
      color: "#2196F3",
      image: "",
    },
    {
      id: "mad-sad-glad",
      name: "Mad, Sad, Glad",
      description:
        "Express emotions about the sprint - what made you mad, sad, or glad?",
      imageKey:
        "dev/OTHERS/962e5384-89e7-41c3-ba6b-ad697a8d0e64/6825874c015624d10f1308f0/Screenshot 2025-09-03 at 6.29.13 PM_1756904437139_elc94i6y4dm.png",
      color: "#FF9800",
      image: "",
    },
    {
      id: "sailboat",
      name: "Sailboat Retrospective",
      description:
        "Visualize your journey with anchors (obstacles), rocks (risks), and wind (helping factors).",
      imageKey:
        "dev/OTHERS/962e5384-89e7-41c3-ba6b-ad697a8d0e64/68104f17c390c258326a855b/sailBoat.png",
      color: "#9C27B0",
      image: "",
    },
    // {
    //   id: "timeline",
    //   name: "Timeline Retrospective",
    //   description:
    //     "Map events chronologically to identify patterns and key moments in the sprint.",
    //   imageKey:
    //     "dev/OTHERS/962e5384-89e7-41c3-ba6b-ad697a8d0e64/6825874c015624d10f1308f0/Screenshot 2025-09-03 at 6.29.13 PM_1756904437139_elc94i6y4dm.png",
    //   color: "#607D8B",
    //   image: "",
    // },
  ];

  // Fetch image URLs when component mounts
  useEffect(() => {
    const fetchTemplateImages = async () => {
      const updatedTemplates = [...initialTemplates];
      const loadingStates: { [key: string]: boolean } = {};

      // Set initial loading states
      for (const template of updatedTemplates) {
        if (template.imageKey) {
          loadingStates[template.id] = true;
        }
      }
      setLoadingImages(loadingStates);

      // Set templates immediately so UI can render with skeletons
      setTemplates(updatedTemplates);

      // Create promises for all image fetches
      const imagePromises = updatedTemplates.map(async (template, index) => {
        if (template.imageKey) {
          try {
            //console.log(`Fetching image for template: ${template.name}`);
            const response = await postAttachmentKey({
              key: template.imageKey,
            }).unwrap();

            //console.log(`Response for ${template.name}:`, response);

            const displayUrl =
              response?.data?.decryptedData?.url ||
              response?.data?.url ||
              response?.url ||
              "";

            if (displayUrl) {
              //console.log(`Image URL for ${template.name}: ${displayUrl}`);
              updatedTemplates[index].image = displayUrl;
            } else {
              console.warn(`No image URL found for template: ${template.name}`);
            }
          } catch (error) {
            console.error(
              `Error fetching attachment URL for template ${template.id}:`,
              error,
            );
          } finally {
            setLoadingImages((prev) => ({ ...prev, [template.id]: false }));
          }
        }
        return template;
      });

      try {
        await Promise.all(imagePromises);
        //console.log("All images processed:", updatedTemplates);
        setTemplates([...updatedTemplates]); // Force re-render with new template data
        setTemplatesLoaded(true);
      } catch (error) {
        console.error("Error processing template images:", error);
        setTemplatesLoaded(true); // Still set to true to show the templates
      }
    };

    fetchTemplateImages();
  }, [postAttachmentKey]);

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplate(templateId);
  };

  const handleContinue = () => {
    if (selectedTemplate) {
      onTemplateSelect(selectedTemplate, selectedSprintId);
    }
  };

  const renderTemplateImage = (template: Template) => {
    const isLoading = loadingImages[template.id];

    if (isLoading) {
      return (
        <Skeleton
          variant="rectangular"
          width="100%"
          height="100%"
          sx={{ borderRadius: "12px" }}
        />
      );
    }

    if (template.image) {
      return (
        <img
          src={template.image}
          alt={template.name}
          style={{
            width: "100%",
            height: "100%",
            objectFit: viewMode === "grid" ? "cover" : "cover", // Changed from "fill" to "cover"
            borderRadius: "12px",
          }}
          onLoad={() => {
            //console.log(`Image loaded successfully for: ${template.name}`);
          }}
          onError={(e) => {
            console.error(`Image failed to load for: ${template.name}`, e);
            const target = e.target as HTMLImageElement;
            target.style.display = "none";
          }}
        />
      );
    }

    // Fallback - show a placeholder or nothing
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          backgroundColor: template.color || "#f5f5f5",
          borderRadius: "12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No Image
        </Typography>
      </Box>
    );
  };

  const renderSkeletonCard = () => (
    <Card
      sx={{
        borderRadius: 0,
        boxShadow: "none",
        border: "1px solid #e0e0e0",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              mb: 2,
            }}
          >
            <Skeleton
              variant="circular"
              width={20}
              height={20}
              sx={{ mr: 2, mt: 0.5 }}
            />
            <Box sx={{ flexGrow: 1 }}>
              <Skeleton variant="text" width="60%" height={32} sx={{ mb: 1 }} />
              <Skeleton
                variant="text"
                width="90%"
                height={20}
                sx={{ mb: 0.5 }}
              />
              <Skeleton variant="text" width="70%" height={20} />
            </Box>
          </Box>
          <Box
            sx={{
              width: "100%",
              height: 200,
              borderRadius: "12px",
              ml: 5,
            }}
          >
            <Skeleton
              variant="rectangular"
              width="100%"
              height="100%"
              sx={{ borderRadius: "12px" }}
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const renderSkeletonListItem = () => (
    <Card
      sx={{
        borderRadius: 0,
        boxShadow: "none",
        border: "1px solid #e0e0e0",
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              mb: 2,
            }}
          >
            <Skeleton
              variant="circular"
              width={20}
              height={20}
              sx={{ mr: 3, mt: 0.5, flexShrink: 0 }}
            />
            <Box sx={{ flexGrow: 1 }}>
              <Skeleton variant="text" width="40%" height={32} sx={{ mb: 1 }} />
              <Skeleton
                variant="text"
                width="80%"
                height={20}
                sx={{ mb: 0.5 }}
              />
              <Skeleton variant="text" width="60%" height={20} />
            </Box>
          </Box>
          <Box
            sx={{
              width: "100%",
              maxWidth: 700,
              height: 300,
              borderRadius: "12px",
              ml: 5,
            }}
          >
            <Skeleton
              variant="rectangular"
              width="100%"
              height="100%"
              sx={{ borderRadius: "12px" }}
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 3, width: "100%" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 4,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton onClick={onBack} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
              {t("Choose Template")}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t("Select a retrospective template for")}{" "}
              <strong>{selectedSprintName}</strong>
            </Typography>
          </Box>
        </Box>
        {/* View Toggle */}
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, newViewMode) => {
            if (newViewMode !== null) {
              setViewMode(newViewMode);
            }
          }}
          size="small"
        >
          <ToggleButton value="grid" aria-label="grid view">
            <ViewModuleIcon />
          </ToggleButton>
          <ToggleButton value="list" aria-label="list view">
            <ViewListIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Template Selection */}
      <FormControl component="fieldset" sx={{ width: "100%" }}>
        <RadioGroup
          value={selectedTemplate}
          onChange={(e) => handleTemplateSelect(e.target.value)}
        >
          <Box
            sx={{
              height: "calc(100vh - 300px)",
              overflowY: "auto",
              pr: 1,
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: theme.palette.grey[100],
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: theme.palette.grey[400],
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: theme.palette.grey[500],
                },
              },
            }}
          >
            {viewMode === "grid" ? (
              <Grid container spacing={3}>
                {!templatesLoaded
                  ? // Show skeleton cards while loading
                    Array.from({ length: 6 }).map((_, index) => (
                      <Grid
                        item
                        xs={12}
                        md={6}
                        lg={4}
                        key={`skeleton-${index}`}
                      >
                        {renderSkeletonCard()}
                      </Grid>
                    ))
                  : // Show actual template cards when loaded
                    templates.map((template) => (
                      <Grid item xs={12} md={6} lg={4} key={template.id}>
                        <Card
                          sx={{
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            border:
                              selectedTemplate === template.id
                                ? `2px solid ${template.color}`
                                : "1px solid #e0e0e0",
                            borderRadius: 0,
                            boxShadow: "none",
                            "&:hover": {
                              border: `1px solid ${theme.palette.grey[800]}`,
                            },
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                          }}
                          onClick={() => handleTemplateSelect(template.id)}
                        >
                          <CardContent sx={{ flexGrow: 1, p: 3 }}>
                            <Box sx={{ mb: 2 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  mb: 2,
                                }}
                              >
                                <FormControlLabel
                                  value={template.id}
                                  control={<Radio />}
                                  label=""
                                  sx={{ m: 0, mr: 2 }}
                                />
                                <Box sx={{ flexGrow: 1 }}>
                                  <Typography
                                    variant="h6"
                                    fontWeight={600}
                                    sx={{ mb: 1 }}
                                  >
                                    {template.name}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ lineHeight: 1.6 }}
                                  >
                                    {template.description}
                                  </Typography>
                                </Box>
                              </Box>
                              <Box
                                sx={{
                                  width: "100%",
                                  height: 200,
                                  borderRadius: "12px",
                                  // backgroundColor: template.color,
                                  overflow: "hidden",
                                  position: "relative",
                                }}
                              >
                                {renderTemplateImage(template)}
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
              </Grid>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  marginBottom: 12,
                }}
              >
                {!templatesLoaded
                  ? // Show skeleton list items while loading
                    Array.from({ length: 6 }).map((_, index) => (
                      <div key={`skeleton-list-${index}`}>
                        {renderSkeletonListItem()}
                      </div>
                    ))
                  : // Show actual template list items when loaded
                    templates.map((template) => (
                      <Card
                        key={template.id}
                        sx={{
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                          border:
                            selectedTemplate === template.id
                              ? `3px solid ${template.color}`
                              : "1px solid #e0e0e0",
                          borderRadius: 0,
                          boxShadow: "none",
                          "&:hover": {
                            transform: "translateX(2px)",
                            border: `1px solid ${theme.palette.grey[400]}`,
                          },
                        }}
                        onClick={() => handleTemplateSelect(template.id)}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                mb: 2,
                              }}
                            >
                              <FormControlLabel
                                value={template.id}
                                control={<Radio />}
                                label=""
                                sx={{ m: 0, mr: 3, flexShrink: 0 }}
                              />
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography
                                  variant="h6"
                                  fontWeight={600}
                                  sx={{ mb: 1 }}
                                >
                                  {template.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ lineHeight: 1.6 }}
                                >
                                  {template.description}
                                </Typography>
                              </Box>
                            </Box>
                            <Box
                              sx={{
                                width: "100%",
                                maxWidth: 700,
                                height: 300,
                                borderRadius: "12px",
                                // backgroundColor: template.color,
                                overflow: "hidden",
                                position: "relative",
                              }}
                            >
                              {renderTemplateImage(template)}
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
              </Box>
            )}
          </Box>
        </RadioGroup>
      </FormControl>

      {/* Action Buttons */}
      <Box
        sx={{
          position: "sticky",
          backgroundColor: "white",
          display: "flex",
          justifyContent: "space-between",
          mt: 4,
          pt: 3,
          pb: 2,
          zIndex: 1000,
        }}
      >
        <Button
          variant="outlined"
          onClick={onBack}
          startIcon={<ArrowBackIcon />}
          sx={{ px: 3 }}
        >
          {t("Back to Table")}
        </Button>
        <Button
          variant="contained"
          onClick={handleContinue}
          disabled={!selectedTemplate}
          sx={{ px: 4, py: 1.5 }}
        >
          {t("Start Retrospective")}
        </Button>
      </Box>
    </Box>
  );
};

export default TemplateSelectionPage;
